/* eslint-disable max-lines */
import { FC, useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { CurvePoint, OURS, PriceCurve, colorOf } from "../../../utils/esim/priceCurve";
import { gb, money } from "../../../utils/esim/margin";
import Box from "../../ui/Box";

const INK_MUTED = "#898781";
const GRIDLINE = "#e1e0d9";
const SURFACE = "#ffffff";

type ChartRow = Record<string, number | string | CurvePoint | null>;

interface Props {
  curve: PriceCurve;
}

// A seller whose figures are not in euros says so in its legend entry.
const legendName = (curve: PriceCurve, seller: string) => {
  const currency = curve.sellerCurrency[seller];
  return currency && currency !== "EUR" ? `${seller} (${currency})` : seller;
};

// One readout for every seller at the hovered size, value first; the plan behind it in muted ink.
const Readout: FC<{ point: CurvePoint; curve: PriceCurve }> = ({ point, curve }) => (
  <Box padding="px-4 py-3" className="text-sm leading-5 text-black outline-none min-w-[220px]">
    <p className="font-medium mb-1.5">të paktën {point.sizeGb} GB</p>
    {curve.sellers.map((seller) => {
      const offer = point.offers[seller];
      return (
        <p key={seller} className="flex items-center gap-2 whitespace-nowrap">
          <span
            className="inline-block w-3 h-0.5 rounded"
            style={{ backgroundColor: colorOf(seller) }}
            aria-hidden
          />
          <span className={point.cheapest === seller ? "font-bold" : "font-medium"}>
            {offer ? money(offer.price, offer.currency) : "–"}
          </span>
          <span>{seller}</span>
          {offer && (
            <span className="text-neutral text-xs truncate max-w-[220px]" title={offer.plan}>
              {gb(offer.dataGb)}, {offer.days ?? "?"} ditë
            </span>
          )}
        </p>
      );
    })}
  </Box>
);

const PriceCurveChart: FC<Props> = ({ curve }) => {
  const data = useMemo(
    () =>
      curve.points.map((point) => {
        const row: ChartRow = { label: `${point.sizeGb} GB`, point };
        curve.sellers.forEach((seller) => {
          const offer = point.offers[seller];
          row[seller] = offer ? offer.price : null;
        });
        return row;
      }),
    [curve]
  );

  return (
    <div className="w-full">
      {/* Our own legend: a short line key in the series colour, the name in plain ink, one row. */}
      <ul className="flex flex-wrap justify-end gap-x-5 gap-y-1 mb-2 text-xs text-neutral">
        {curve.sellers.map((seller) => (
          <li key={seller} className="flex items-center gap-2">
            <span
              className={`inline-block w-4 rounded ${seller === OURS ? "h-[3px]" : "h-0.5"}`}
              style={{ backgroundColor: colorOf(seller) }}
              aria-hidden
            />
            <span className={seller === OURS ? "font-medium text-black" : ""}>
              {legendName(curve, seller)}
            </span>
          </li>
        ))}
      </ul>
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={data} margin={{ top: 12, right: 24, bottom: 4, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRIDLINE} strokeWidth={1} />
          <XAxis
            dataKey="label"
            axisLine={{ stroke: GRIDLINE }}
            tickLine={false}
            fontSize={12}
            stroke={INK_MUTED}
            dy={8}
          />
          <YAxis axisLine={false} tickLine={false} fontSize={12} stroke={INK_MUTED} width={48} />
          <Tooltip
            cursor={{ stroke: INK_MUTED, strokeWidth: 1 }}
            wrapperStyle={{ outline: "none" }}
            content={({ active, payload }) =>
              active && payload && payload.length ? (
                <Readout point={payload[0].payload.point as CurvePoint} curve={curve} />
              ) : null
            }
          />
          {curve.sellers.map((seller) => (
            <Line
              key={seller}
              type="linear"
              dataKey={seller}
              name={legendName(curve, seller)}
              stroke={colorOf(seller)}
              strokeWidth={seller === OURS ? 3 : 2}
              strokeLinejoin="round"
              strokeLinecap="round"
              dot={{ r: 4, strokeWidth: 2, stroke: SURFACE, fill: colorOf(seller) }}
              activeDot={{ r: 6, strokeWidth: 2, stroke: SURFACE }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PriceCurveChart;
